<script setup lang="ts">
import { computed } from "vue";
import { type ImgSize, type TImgInput, useImgProxy } from "@/composables/shared/useImageProxy.ts";
import { useDefaultPlaceholder, useErrorPlaceholder } from "@/utils/DefaultImgPlaceholder.ts";

const props = defineProps<{
  src?: TImgInput;
  alt?: string;
  size?: ImgSize;
  lazySrc?: string;
  width?: number;
  height?: number;
  maxWidth?: string;
  maxHeight?: string;
  minWidth?: string;
  minHeight?: string;
  cover?: boolean;
  eager?: boolean;
}>();

const { getImgProxyUrl } = useImgProxy();
const defaultPlaceholder = useDefaultPlaceholder();
const errorPlaceholder = useErrorPlaceholder();

const placeholder = computed(() => props.lazySrc ?? defaultPlaceholder.value);
</script>

<template>
  <div
    v-if="!src"
    class="tw:flex tw:items-center tw:justify-center"
    :style="{
      width: width ? width : undefined,
      height: height ? height : undefined,
      maxWidth: maxWidth ? maxWidth : undefined,
      maxHeight: maxHeight ? maxHeight : undefined,
    }"
  >
    <slot name="missing">
      <div class="tw:flex tw:items-center tw:gap-2 tw:text-medium-emphasis">
        <v-icon icon="mdi-image-off-outline" />
      </div>
    </slot>
  </div>

  <v-img
    v-else
    :height="height"
    :width="width"
    :max-width="maxWidth"
    :max-height="maxHeight"
    :min-width="minWidth"
    :min-height="minHeight"
    :cover="cover"
    :eager="eager"
    :alt="alt"
    :src="getImgProxyUrl(src, size ?? 'md')"
    :lazy-src="placeholder"
    class="tw:relative"
  >
    <template #placeholder>
      <div class="tw:relative tw:h-full tw:w-full">
        <img
          :src="placeholder"
          alt=""
          class="tw:absolute tw:inset-0 tw:h-full tw:w-full"
        />
        <div class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          <v-progress-circular
            color="secondary"
            indeterminate
          />
        </div>
      </div>
    </template>

    <template #error>
      <img
        :src="errorPlaceholder"
        alt=""
        class="tw:h-full tw:w-full"
      />
    </template>

    <div class="tw:absolute tw:inset-0 tw:z-content-overlay">
      <slot name="overlay" />
    </div>
  </v-img>
</template>
